/**
 * NotificationPanel.jsx
 * CCH Care — Phase 2 — Layer 6
 * Last Modified: 2026-04-22
 *
 * Slide-out panel listing all notifications.
 * Accessed via bell icon in header.
 *
 * Changes (2026-04-22):
 * - Replaced broken `COLORS.primaryLight + '40'` pattern (produced
 *   invalid CSS like "var(--color-primary-light)40") with
 *   COLORS.primaryLight directly. Unread notification background is
 *   now slightly more visible than before; previous state was
 *   effectively invisible. If it reads too loud after testing, add a
 *   `highlightSubtle` token to constants.js rather than reintroducing
 *   the hex-concat anti-pattern.
 */
import { COLORS } from '../../../config/constants';

import { formatDate } from '../../../utils/formatters';
const TYPE_ICONS = {
  adl_overdue:     '⚠️',
  alert_change:    '🔔',
  care_alert:      '🚨',
  shift_reminder:  '⏰',
  handoff_note:    '📝',
  new_admission:   '🆕',
  system:          'ℹ️',
};

export default function NotificationPanel({
  open, onClose, notifications, unreadCount,
  onMarkRead, onMarkAllRead, onTapNotification,
}) {
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClose()}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.3)', zIndex: 150,
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(360px, 85vw)',
        background: COLORS.surface, zIndex: 160,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 16px rgba(0,0,0,0.12)',
        animation: 'panelSlide 0.2s ease-out',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>Notifications</div>
            {unreadCount > 0 && (
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>{unreadCount} unread</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllRead}
                style={{
                  padding: '6px 12px', borderRadius: 6,
                  border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                  color: COLORS.primary, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', fontSize: 20,
                cursor: 'pointer', color: COLORS.textMuted, padding: '4px 8px',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Notification list */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {notifications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔔</div>
              <div>No notifications</div>
            </div>
          ) : (
            notifications.map(n => (
              <div
                key={n.notification_id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!n.is_read) onMarkRead(n.notification_id);
                  onTapNotification && onTapNotification(n);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    if (!n.is_read) onMarkRead(n.notification_id);
                    onTapNotification && onTapNotification(n);
                  }
                }}
                style={{
                  padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}`,
                  background: n.is_read ? COLORS.surface : COLORS.primaryLight,
                  cursor: 'pointer', display: 'flex', gap: 10,
                }}
              >
                <span style={{ fontSize: 18 }}>{TYPE_ICONS[n.type] || 'ℹ️'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: n.is_read ? 500 : 700,
                    color: COLORS.text, marginBottom: 2,
                  }}>
                    {n.title}
                  </div>
                  <div style={{
                    fontSize: 12, color: COLORS.textMuted,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {n.message}
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 4 }}>
                    {formatRelativeTime(n.created_at)}
                  </div>
                </div>
                {!n.is_read && (
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: COLORS.primary, flexShrink: 0, marginTop: 4,
                  }} />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <style>{`
        @keyframes panelSlide {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}

function formatRelativeTime(isoString) {
  try {
    const d    = new Date(isoString);
    const now  = new Date();
    const mins = Math.floor((now - d) / 60000);
    if (mins < 1)  return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return formatDate(d);
  } catch {
    return '';
  }
}
